<template>
  <div>
    <b-button 
      :disabled="isBusy"
      size="sm"
      :variant="variant"
      @click="handleManualSync">
      <span v-if="countdown.show">
        {{ countdown.count }}sec tot automatische synchronisatie
      </span>
      <span v-else>
        <b-icon
          icon="arrow-repeat"
          class="mr-1"
          :animation="animation"
        />
        {{ buttonlabel }}
      </span>
    </b-button>
    <b-button v-if="countdown.show" size="sm" class="ml-2">
      <b-icon 
        icon="stop-fill" 
        @click="cancelAutoSync" />
    </b-button>
    <b-button v-if="stopped" size="sm">
      <b-icon 
        icon="play-fill" 
        @click="resumeAutoSync" />
    </b-button>
  </div>
</template>

<script> 
import { municipalityOptions } from '@/config'

import { mapGetters, mapActions, mapMutations } from 'vuex'

let announcementTimer = null
let timer = null
let countdownTimer = null
let feedbackTimer = null

const feedbackDuration = 3000

export default {
  data() {
    return {
      // When offline / in another tab the timer continues, but the actual 
      // refresh is postponed until the user returns / reconnects to the internet
      postponedSync: false,

      // The countdown timer shown to the user
      countdown: {
        show: false,
        count: 30
      },

      // Whether the auto sync was manually stopped (during the countdown)
      stopped: false,

      // The btn variant ('secondary', 'danger', 'warning', 'success', etc.)
      variant: 'secondary'
    }
  },
  computed: {
    ...mapGetters('synchronisation', [
      "isBusy",
      "getFromTimestamp",
      "getInterval"
    ]),
    ...mapGetters('user', [
      'roleDetailsMerged'
    ]),
    ...mapGetters('realisation', [
      'hasCurrentlyOpenedRecord',
      'currentlyOpenedRecord',
      'recordByUuid'
    ]),

    ...mapGetters('requests', [
      'hasCurrentlyOpenedRequest',
      'currentlyOpenedRequest',
      'requestByUuid'
    ]),
    animation() {
      return this.isBusy ? 'spin' : 'none'
    },
    // The minimum interval is set to 10 minutes
    interval() {
      return Math.max(this.getInterval || 0, (5 * 60 * 1000))
    },
    intervalInMinutes() {
      return this.interval / (60 * 1000)
    },
    buttonlabel() {
      if (this.variant === 'danger') {
        return 'Synchronisatie mislukt'
      }
      if (this.variant === 'success') {
        return 'Synchronisatie geslaagd'
      }
      if (this.variant === 'warning') {
        return 'Synchronisatie conflict opgemerkt'
      }

      return 'Data synchroniseren'
    }
  },
  created() {
    this.startTimer()
    // this.$notify({
    //   info: true,
    //   title: 'Automatische synchronisatie',
    //   message: `Iedere ${this.intervalInMinutes} minuten wordt een automatische synchronisatie van de data uitgevoerd. 30 seconden voor de synchronisatie verschijnt een notificatie.`,
    //   delay: 10000
    // })
    window.addEventListener('online', this.checkForPostponedSync)
    document.addEventListener('visibilitychange', this.checkForPostponedSync)
  },
  beforeDestroy() {
    this.stopTimers()
    window.removeEventListener('online', this.checkForPostponedSync)
    document.removeEventListener('visibilitychange', this.checkForPostponedSync)
  },
  methods: {
    ...mapActions('requests', [
      'syncRequestRecords'
    ]),
    ...mapActions('realisation', [
      'loadProcessRecordsFromCBAPI'
    ]),
    ...mapMutations('synchronisation', [
      'setAtTimestamp',
      'setFromTimestamp',
      'toggleBusy'
    ]),
    ...mapMutations('relations', [
      'resetRelationsByRealisation',
      'addConnection',
    ]),
    startTimer() {
      timer = setTimeout(this.automatedSync, this.interval)
      announcementTimer = setTimeout(this.announceSync, (this.interval - 30 * 1000))
    },
    stopTimers() {
      clearTimeout(timer)
      clearTimeout(announcementTimer)
      clearTimeout(feedbackTimer)
      clearInterval(countdownTimer)
      this.countdown.show = false
      this.variant = 'secondary'
    },

    cancelAutoSync() {
      this.stopTimers()
      this.stopped = true

      this.$notify({
        warning: true,
        title: 'Automatische synchronisatie gestopt',
        message: 'De automatische synchronisatie van data is gestopt. Let op dat hierdoor de kans op synchronisatie problemen aanzienlijk toeneemt.'
      })
    },
    resumeAutoSync() {
      this.stopped = false
      this.startTimer()

      this.$notify({
        info: true,
        title: 'Automatische synchronisatie gestopt',
        message: `De automatische synchronisatie van data is hervat. Om de ${this.intervalInMinutes} minuten wordt de data gesynchroniseerd.`
      })
    },
    
    // If the user went offline, the automated sync was postponed untill the user goes online again
    async checkForPostponedSync() {
      if ((! document.hidden) && this.postponedSync) {
        this.postponedSync = false
        this.announceSync()
        timer = setTimeout(this.automatedSync, 30 * 1000)
      }
    },

    /**
     * Inform use about upcoming automated data sync
     */
    announceSync() {
      // Start countdown, unless already syncing 
      if (this.countdown.show === false && ! this.isBusy) {
        this.countdown.show = true
        this.countdown.count = 30
        countdownTimer = setInterval(this.announceSync, 1000) // 1 second
      // Countdown ended
      } else if (this.countdown.count === 1) {
        clearInterval(countdownTimer)
        this.countdown.show = false
        this.countdown.count = 30

      // Reduce countdown by 1
      } else {
        this.countdown.count = this.countdown.count - 1
      }
    },

    clearFeedbackColor() {
      this.variant = 'secondary'
    },

    async automatedSync() {
      if ((! navigator.onLine) || (document.hidden)) {
        this.postponedSync = true
      } else {
        await this.handleManualSync({ auto: true })
      }
    },
    async handleManualSync({ auto = false } = { auto: false }) {

      this.stopTimers()

      if (this.isBusy) return 
      this.toggleBusy()

      try {
        // Compile the list of relevant municipality codes
        // Start with the codes available to the tenant
        let codes = municipalityOptions
          .filter(muni => muni.disabled === false)
          .map(muni => muni.value)

        // Then narrow it down according to the user access
        if (! this.roleDetailsMerged.includes('*')) {
          codes = codes.filter(code => this.roleDetailsMerged.includes(code))
        }

        const at = Math.floor(Date.now())
        this.setAtTimestamp({ ts: at })

        // store for use after refresh
        let timestampOfCurrentlyOpenedProcess = null
        if (this.hasCurrentlyOpenedRecord) {
          timestampOfCurrentlyOpenedProcess = this.recordByUuid({ uuid: this.currentlyOpenedRecord })?.getSyncedTS()
        }
        let timestampOfCurrentlyOpenedRequest = null
        if (this.hasCurrentlyOpenedRequest) {
          timestampOfCurrentlyOpenedRequest = this.requestByUuid({ uuid: this.currentlyOpenedRequest })?.getSyncedTS()
        }


        // GO GO GO
        const result = await Promise.all([
          await this.syncRequestRecords({
            codes, 
            at,
            fromTimeStamp: this.getFromTimestamp
          }),
          await this.loadProcessRecordsFromCBAPI({
            codes,
            at,
            fromTimeStamp: this.getFromTimestamp,
            refresh: true
          })
        ])

        if (! Array.isArray(result[0]) || ! Array.isArray(result[1])) {
          this.$notify({
            error: true,
            title: 'Mislukt',
            message: `De ${auto ? 'automatische ' : ''}data synchronisatie is mislukt. Probeer het eerst nog eens. Herlaad anders de applicatie of neem contact op met de support afdeling.`
          })

          this.variant = 'danger'
          feedbackTimer = setTimeout(this.clearFeedbackColor, feedbackDuration * 2)
        } else {

          // The realisation processes
          result[1].forEach(record => {
            // Clear old relations
            this.resetRelationsByRealisation({ uuid: record.getUuid() })

            // Set the current relations
            ;(record.requestUuids || []).forEach(uuid => {
              this.addConnection({
                requestUuid: uuid,
                realisationUuid: record.getUuid(),
              })
            })
          })


          this.variant = 'success'
          feedbackTimer = setTimeout(this.clearFeedbackColor, feedbackDuration)

          // this.$notify({
          //   success: true,
          //   title: 'Succes',
          //   message: `De ${auto ? 'automatische ' : ''}data synchronisatie is geslaagd.`
          // })

          // go through the synced records to see whether the currently opened record was reloaded
          if (this.hasCurrentlyOpenedRecord) {
            // If one of the synced records is the opened record, and the TS does not match
            // then show a warning
            if ( 
              result[1]
                .filter(record => {
                  console.log(
                    record.getUuid() === this.currentlyOpenedRecord 
                    && record.getSyncedTS() !== timestampOfCurrentlyOpenedProcess
                  )

                  return record.getUuid() === this.currentlyOpenedRecord 
                    && record.getSyncedTS() !== timestampOfCurrentlyOpenedProcess
                })
                .length !== 0
            ) {
              this.$notify({
                warning: true,
                title: "Let op!",
                message: 
                  "Het realisatieproces dat u momenteel heeft geopend is door iemand anders aangepast. Ga terug naar de lijstweergave en open het proces opnieuw om alle invoervelden te actualiseren.",
                position: 'b-toaster-top-center',
                noAutoHide: true
              })

              this.variant = 'warning'
              feedbackTimer = setTimeout(this.clearFeedbackColor, feedbackDuration * 2)
            }
          }
          if (this.hasCurrentlyOpenedRequest) {
            // If one of the synced records is the opened record, and the TS does not match
            // then show a warning
            if ( 
              result[0]
                .filter(record => {
                  console.log(
                    record.getUuid() === this.currentlyOpenedRequest 
                    && record.getSyncedTS() !== timestampOfCurrentlyOpenedRequest
                  )

                  return record.getUuid() === this.currentlyOpenedRequest 
                    && record.getSyncedTS() !== timestampOfCurrentlyOpenedRequest
                })
                .length !== 0
            ) {
              this.$notify({
                warning: true,
                title: "Let op!",
                message: 
                  "De aanvraag die u momenteel heeft geopend is door iemand anders aangepast. Ga terug naar de lijstweergave en open de aanvraag opnieuw om alle invoervelden te actualiseren.",
                position: 'b-toaster-top-center',
                noAutoHide: true
              })

              this.variant = 'warning'
              feedbackTimer = setTimeout(this.clearFeedbackColor, feedbackDuration * 2)
            }
          }

          this.setFromTimestamp({ ts: at })
        }
      } catch(err) {
        this.$notify({
          error: true,
          title: 'Synchronisatie mislukt',
          message: 'De incrementele data synchronisatie is mislukt. Herlaad de applicatie om de actuele data in te laden.'
        })
        console.log(err)

        this.variant = 'danger'
        feedbackTimer = setTimeout(this.clearFeedbackColor, feedbackDuration * 2)
      }

      // And we're done
      this.toggleBusy()

      // Do not start the timer if the auto sync was disabled
      if (! this.stopped) {
        this.startTimer()
      }
    }
  }
}

</script>

